import React, { useState, useEffect } from 'react';
import {
    Users, UserPlus, Shield, MapPin, Mail, Key,
    Trash2, X, CheckCircle, Search, MoreHorizontal, ShieldCheck
} from 'lucide-react';
import api from '../api/axiosInstance';

const StaffManagement = () => {
    const [staff, setStaff] = useState([]);
    const [branches, setBranches] = useState([]);
    const [roles, setRoles] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role_id: '',
        branch_id: ''
    });

    useEffect(() => {
        fetchStaffData();
    }, []);

    const fetchStaffData = async () => {
        try {
            const [staffRes, branchRes, roleRes] = await Promise.all([
                api.get('get_staff.php'),
                api.get('get_branches.php'),
                api.get('get_roles.php')
            ]);
            setStaff(staffRes.data.staff);
            setBranches(branchRes.data.branches);
            setRoles(roleRes.data.roles);
        } catch (err) {
            console.error("Error loading staff data");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('create_staff.php', formData);
            if (response.data.status === "success") {
                alert("Staff account created!");
                setIsModalOpen(false);
                fetchStaffData();
            }
        } catch (err) {
            alert(err.response?.data?.message || "Error creating user");
        }
    };

    const handleResetPassword = async (userId) => {
        const newPass = window.prompt("Enter new password for this user:");
        if (newPass) {
            try {
                await api.post('reset_staff_password.php', { user_id: userId, new_password: newPass });
                alert("Password updated successfully.");
            } catch (err) {
                alert("Reset failed.");
            }
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Staff & Team</h1>
                    <p className="text-slate-500 font-medium italic">Manage permissions and office access.</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-indigo-100 transition-all flex items-center gap-2"
                >
                    <UserPlus size={18} /> Onboard Staff
                </button>
            </div>

            {/* Staff Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {staff.map((member) => (
                    <div key={member.id} className="bg-white rounded-[40px] border border-slate-100 p-8 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
                        <div className="flex justify-between items-start mb-6">
                            <div className="w-16 h-16 rounded-3xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl font-black border border-indigo-100 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                {member.username[0].toUpperCase()}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => handleResetPassword(member.id)} className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-xl transition-all" title="Reset Password">
                                    <Key size={18} />
                                </button>
                                <button className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="mb-6">
                            <h3 className="text-xl font-black text-slate-900 leading-none">{member.username}</h3>
                            <div className="flex items-center gap-2 text-slate-400 mt-2">
                                <Mail size={14} />
                                <span className="text-xs font-medium">{member.email}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-6 border-t border-slate-50">
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={14} className="text-indigo-500" />
                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{member.role_name}</span>
                            </div>
                            <div className="flex items-center gap-2 justify-end">
                                <MapPin size={14} className="text-slate-400" />
                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest truncate max-w-[80px]">{member.branch_name}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* --- ADD STAFF MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md" onClick={() => setIsModalOpen(false)}></div>
                    <div className="bg-white w-full max-w-lg rounded-[48px] shadow-2xl relative z-10 p-10 border border-white">
                        <h2 className="text-2xl font-black text-slate-900 mb-8 italic">New Staff Profile</h2>

                        <form onSubmit={handleCreateUser} className="space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    type="text" placeholder="Username" required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 outline-none focus:ring-4 focus:ring-indigo-50"
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                />
                                <select
                                    required className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 font-bold text-slate-600"
                                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                                >
                                    <option value="">Select Role</option>
                                    {roles.map(r => <option key={r.id} value={r.id}>{r.role_name}</option>)}
                                </select>
                            </div>

                            <input
                                type="email" placeholder="Email Address" required
                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 outline-none"
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />

                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    type="password" placeholder="Password" required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 outline-none"
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                                <select
                                    required className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 font-bold text-slate-600"
                                    onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                                >
                                    <option value="">Select Branch</option>
                                    {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                                </select>
                            </div>

                            <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-5 rounded-3xl transition-all shadow-xl shadow-indigo-100 mt-4">
                                Deploy User Account
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffManagement;